import React, { useState, useEffect, useMemo } from "react";
import { startCase } from "lodash";
import clsx from "clsx";
import navigate from "@utils/navigateWithParams";
import { AnimatePresence, m } from "framer-motion";
import { CardProduct, StoreSearch } from "@molecules";
import useAllProductsData from "@shopify/staticQueries/AllProductsQuery";
import useAllCollectionsData from "@shopify/staticQueries/AllCollectionsQuery";
import useAllIngredientsData from "@shopify/staticQueries/AllIngredientsQuery";
import useAllIndicationsData from "@shopify/staticQueries/AllIndicationsQuery";
import useAllPatternsData from "@shopify/staticQueries/AllPatternsQuery";
import useAllActionsData from "@shopify/staticQueries/AllActionsQuery";
import { Icon, Button } from "@atoms";
import { useAppState } from "@state";
import getAllParams from "@utils/getAllParams";
import FilterSelect from "../molecules/FilterSelect";

const ProductList = ({
  collection,
  hideFilters,
  childCollection,
  initialState,
  customer,
  recommendationsOnly,
}) => {
  const url = typeof window !== "undefined" ? window.location.href : null;
  const [productSet, setProductSet] = useState(initialState || "all");
  const products = useAllProductsData();
  const collections = useAllCollectionsData();
  const ingredients = useAllIngredientsData();
  const indications = useAllIndicationsData();
  const patterns = useAllPatternsData();
  const actions = useAllActionsData();
  const all = useMemo(
    () =>
      [...ingredients, ...indications, ...actions, ...patterns].map(c => ({
        id: c.id,
        title: c?.common || c?.indication || c?.action || c?.pattern,
      })),
    []
  );

  const [
    {
      user,
      filters: stateFilter, // for saved filters
    },
    dispatch,
  ] = useAppState();

  const { filters: savedFilters } = stateFilter;

  const _recommendations =
    customer?.recommendations ||
    user?.customer?.recommendations ||
    user?.info?.recommendations ||
    [];
  const recommendations = Array.isArray(_recommendations)
    ? _recommendations
    : [];
  const favorites = user?.favorites || [];

  const childCollections = {
    productLine: collections
      .filter(c => c.type === "Product Line" && c.children.length)
      .reduce(
        (agg, c) => ({
          [c.handle]: c.children.map(cc => [cc.title, cc.handle]),
        }),
        {}
      ),
  };

  const [visible, setVisible] = useState(30);
  const options = {
    application: [
      ["For Adults", "adults"],
      ["For Pets", "kan-essentials"],
      ["For Children", "gentle-warriors"],
    ],
    pinyin: [
      ...new Set(
        products
          .filter(p => p.chineseName)
          .sort((a, b) => a.title.localeCompare(b.title))
          .filter((elem, index, self) => index === self.indexOf(elem))
      ),
    ].map(p => [
      `${p.title} <span class="text-green text-xs">(${p.chineseName})</span>`,
      p.title,
    ]),
    herb: ingredients
      .filter(c => c.type === "Herb")
      .map(h => [
        h.common
          ? `${h.common} <span class="text-green text-xs">(${h.pinyin})</span>`
          : h.pinyin,
        h.id,
      ])
      .sort((a, b) => a[0].localeCompare(b[0])),
    pattern: patterns
      .map(h => [h.pattern, h.id])
      .sort((a, b) => a[0].localeCompare(b[0])),
    action: actions
      .map(h => [h.action, h.id])
      .sort((a, b) => a[0].localeCompare(b[0])),
    indication: indications
      .map(h => [h.indication, h.id])
      .sort((a, b) => a[0].localeCompare(b[0])),
    type: [
      ["Extract", "extract"],
      ["Tablets", "tablets"],
    ],
    productLine: collections
      .filter(c => c.type === "Product Line")
      .map(h => [h.title, h.handle]),
    // status: [["In Stock", "inStock"]],
    // organic: [["Some organic ingredients", "hasOrganic"]],
  };

  const defaultFilterState = Object.keys(options).reduce((a, b) => {
    const currentCollection = collections.find(
      c => c.handle === collection || c.handle === collection?.parent?.handle
    );
    // set filter state to default if one is set
    if (currentCollection) {
      if (currentCollection.type === "Symptom" && b === "symptom") {
        const arr = [collection];
        if (childCollection) arr.push(childCollection);
        return { ...a, [b]: arr };
      }
      if (currentCollection.type === "Product Line" && b === "productLine") {
        const arr = [collection];
        if (childCollection) arr.push(childCollection);
        return { ...a, [b]: arr };
      }
      if (currentCollection.type === "Herb" && b === "herb") {
        const arr = [collection];
        if (childCollection) arr.push(childCollection);
        return { ...a, [b]: arr };
      }
    }
    return { ...a, [b]: [] };
  }, {});

  const [filters, setFilters] = useState(
    defaultFilterState?.productLine?.length && savedFilters
      ? {
          ...savedFilters,
          productLine: defaultFilterState?.productLine,
        }
      : savedFilters || defaultFilterState
  );

  const [sort] = useState("alpha");

  const sortFunc = {
    alpha: (a, b) => a.title.localeCompare(b.title),
    latest: () => [], // doosomething,
    popular: () => [], // doosomething,
  }[sort];

  const filterFunc = product => {
    const pinyin =
      !filters.pinyin.length || filters.pinyin.includes(product.title);
    const set =
      productSet === "all" ||
      (
        productSet === "recommendations" &&
        recommendations &&
        recommendations
          .map(r => r.id)
          .filter(v =>
            product.variants.map(variant => variant.storefrontId).includes(v)
          )
      ).length ||
      (productSet === "favorites" && favorites.includes(product.storefrontId));
    const type =
      !filters.type.length ||
      filters.type.filter(value => product.types.includes(value)).length;
    // const status = !filters.status.length || product.inStock;
    const application =
      (!filters.application.length ||
        filters.application.includes(product.productLine?.handle) ||
        (filters.application.includes("adults") &&
          !["kan-essentials", "gentle-warriors"].includes(
            product.productLine?.handle
          ))) &&
      filters.application.length < 2;
    // const symptom =
    //   !filters.symptom.length ||
    //   filters.symptom.filter(value =>
    //     product?.symptoms.map(c => c.id).includes(value)
    //   ).length === filters.symptom.length;
    const symptom = true;
    const indication =
      !filters.indication.length ||
      filters.indication.filter(value =>
        product?.indications.map(c => c.id).includes(value)
      ).length === filters.indication.length;
    const productLine =
      !filters.productLine.length ||
      filters.productLine.includes(product.productLine?.handle);
    const productLineChild =
      !filters.productLine.length ||
      filters.productLine.filter(value =>
        product.collections.map(c => c.handle).includes(value)
      ).length === filters.productLine.length;
    const pattern =
      !filters.pattern.length ||
      filters.pattern.filter(value =>
        product?.patterns.map(c => c.id).includes(value)
      ).length === filters.pattern.length;
    const herb =
      !filters.herb.length ||
      filters.herb.filter(value =>
        product?.herbs.map(c => c.id).includes(value)
      ).length === filters.herb.length;
    // const organic =
    //   !filters.organic.length || product.herb?.filters(f => f?.organic).length;
    const action =
      !filters.action.length ||
      filters.action.filter(value =>
        product?.actions.map(c => c.id).includes(value)
      ).length === filters.action.length;
    return (
      // status &&
      application &&
      symptom &&
      indication &&
      productLine &&
      pattern &&
      herb &&
      type &&
      productLineChild &&
      set &&
      pinyin &&
      // organic &&
      action
    );
  };

  const filteredProducts = [...products].filter(filterFunc).sort(sortFunc);

  useEffect(() => {
    const params = getAllParams();

    const keys = [
      // "status",
      "application",
      "symptom",
      "indication",
      "productLine",
      "pattern",
      "herb",
      "type",
      "productLineChild",
      "set",
      "pinyin",
      // "organic",
      "action",
    ];

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of params) {
      if (keys.includes(key)) {
        setFilters(priorFilters => {
          return {
            ...priorFilters,
            [key]: [`gid://shopify/Metaobject/${value}`],
          };
        });
      }
    }
  }, [url]);

  useEffect(() => {
    return () =>
      dispatch({
        type: "setFilters",
        filters:
          JSON.stringify(filters) === JSON.stringify(defaultFilterState)
            ? null
            : filters,
      });
  }, [filters]);

  const hasFiltersActive = !!Object.values(filters).reduce(
    (accumulator, currentValue) => [...accumulator, ...currentValue],
    []
  ).length;

  return (
    <div className="flex flex-col">
      {user.type && !recommendationsOnly && (
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => {
              setProductSet("all");
            }}
            className={clsx("rounded-t p-4 text-sm", {
              "bg-white font-bold": productSet === "all",
              "bg-gray": productSet !== "all",
            })}
          >
            All Products
          </button>
          <button
            type="button"
            onClick={() => {
              if (
                user.level > 1 &&
                (!user.customer || user?.customer?.id === user?.info?.id)
              ) {
                setProductSet("favorites");
              } else {
                setProductSet("recommendations");
              }
            }}
            className={clsx("rounded-t p-4 text-sm", {
              "bg-white font-bold": productSet !== "all",
              "bg-gray": productSet === "all",
            })}
          >
            {user.level > 2 &&
              user.customer &&
              user?.customer?.id !== user?.info?.id &&
              `${user.customer.firstName}'s Recommendations`}
            {user.level > 1 &&
              (!user.customer || user?.customer?.id === user?.info?.id) &&
              "My Favorites"}
            {user.level === 1 && "My Products"}
          </button>
        </div>
      )}
      <div
        className={clsx(
          "rounded-t-r flex w-full flex-col gap-5 rounded-b p-5 pb-10 sm:grid sm:grid-cols-4 md:gap-10",
          {
            "bg-white": user?.type,
          }
        )}
      >
        {/* search */}
        <div
          className={clsx("col-span-2 flex justify-end", {
            "sm:col-start-3 sm:col-end-5": !hideFilters,
          })}
        >
          <StoreSearch />
        </div>

        {/* filters */}
        {!hideFilters && (
          <div className="row-span-3 row-start-1 flex flex-col gap-5">
            <div
              className={clsx({
                "pointer-events-none opacity-0 sm:mb-7": !hasFiltersActive,
              })}
            >
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-bold"
                onClick={() =>
                  collection
                    ? navigate("/products/")
                    : setFilters(defaultFilterState)
                }
              >
                <span className="underline decoration-orange underline-offset-[3px]">
                  Clear All Filters
                </span>
                <Icon
                  name="close"
                  className="h-4 w-4 stroke-orange stroke-1 text-orange"
                />
              </button>
            </div>

            <ul className="flex flex-wrap gap-3 sm:flex-col">
              {hasFiltersActive &&
                Object.entries(filters)?.map(([key, value]) => {
                  const title = {
                    pinyin: "Formula",
                    application: "For",
                    adults: "For Adults",
                    "gentle-warriors": "For Kids",
                    "kan-essentials": "For Pets",
                  };
                  return value?.map(v => {
                    const parent =
                      childCollections?.[key] &&
                      Object.entries(childCollections?.[key])?.map(
                        ([k, arr]) =>
                          arr?.map(e => e[1])?.includes(v) ? k : null
                      )?.[0];

                    return (
                      <li key={v}>
                        <button
                          className="flex items-center justify-between gap-1 rounded-full bg-orange px-3 py-1.5 text-sm text-white"
                          type="button"
                          onClick={() =>
                            setFilters(priorFilters => {
                              return {
                                ...priorFilters,
                                [key]: priorFilters[key].filter(
                                  item => item !== v
                                ),
                              };
                            })
                          }
                        >
                          <span className="text-left leading-tight">
                            <span className="font-bold">
                              {startCase(title[key] || key)}:
                            </span>{" "}
                            {parent
                              ? startCase(
                                  `${parent}: ${startCase(title[v] || v)}`
                                )
                              : startCase(
                                  all?.find(a => a.id === v)?.title ||
                                    title[v] ||
                                    v
                                )}
                          </span>
                          <Icon
                            name="close"
                            className="stroke-1.5 h-4 w-4 stroke-white"
                          />
                        </button>
                      </li>
                    );
                  });
                })}
            </ul>
            {/* <div>
            <button type="button" className="flex items-center gap-1 text-sm">
              <span className="font-bold text-orange">Sort by</span>{" "}
              {
                { alpha: "Name", latest: "Newest", popular: "Most Popular" }[
                  sort
                ]
              }
              <Icon name="chevron" className="h-2 w-2 rotate-90 stroke-2" />
            </button>
          </div> */}

            <ul className="flex flex-col gap-5 ">
              {Object.entries(options).map(([key, value]) => (
                <li key={key}>
                  <FilterSelect
                    state={[filters[key], setFilters]}
                    attribute={key}
                    options={value}
                    childCollections={childCollections[key]}
                    initial={filters[key].length}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* products */}

        <AnimatePresence initial mode="popLayout">
          {!filteredProducts.length && (
            <m.div
              key="noProducts"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="col-span-2 flex flex-col items-center justify-center gap-5 p-10 text-center lg:col-span-3"
            >
              {productSet === "recommendations" && (
                <p>
                  {user.customer
                    ? "Your client has no recommendations that match the selected criteria."
                    : "You have not you have not added nor have been recommended any products."}
                </p>
              )}
              {productSet === "all" && (
                <p>
                  No products matched your filters. Remove some filters to see
                  more products.
                </p>
              )}
              {productSet === "favorites" && <p>You have no saved products.</p>}
            </m.div>
          )}
          {!!filteredProducts.length && (
            <m.ul
              className={clsx(
                "row-start-2 flex flex-col gap-5 sm:grid sm:grid-cols-2 md:grid-cols-3 md:gap-x-8 md:gap-y-10 lg:grid-cols-4",
                {
                  "sm:col-span-4": hideFilters,
                  "col-start-2 sm:col-span-3": !hideFilters,
                }
              )}
              transition={{ delayChildren: 0.5 }}
            >
              {filteredProducts.slice(0, visible).map(p => (
                <m.li
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key={p.id}
                >
                  <CardProduct
                    rx={
                      recommendations.find(r =>
                        p.variants.map(v => v.storefrontId).includes(r.id)
                      )?.rx
                    }
                    recommendationsOnly={recommendationsOnly}
                    typeFilter={filters.type}
                    {...p}
                  />
                </m.li>
              ))}
              {filteredProducts.length > visible && (
                <li className="col-span-2 mt-10 lg:col-span-3">
                  <Button onClick={() => setVisible(s => s + 30)}>
                    View More
                  </Button>
                </li>
              )}
            </m.ul>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ProductList;
